import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ResponseModel } from '../../../../shared/models/base-response.model';
import { UserTerminal } from '../../../../shared/models/lottery/users.models';
import { DeviceService } from '../../../../shared/service/device/device.service';
import { UsersService } from '../../../../shared/service/lottery/users.service';
import { CognitoService } from './cognito.service';
import { DialogsService } from '../../../../shared/service/dialogs.service';
import { DecryptedUser } from '../../../../shared/models/rbac/rbac.models';
import { MatDialog } from '@angular/material/dialog';
import { LogoutModalComponent } from '../components/logout-modal/logout-modal.component';
import { jwtDecode } from 'jwt-decode';
import { RbacService } from '../../../../shared/service/rbac/rbac.service';
import { BehaviorSubject, filter, firstValueFrom, retry } from 'rxjs';
import { AuthMode } from '../../../../shared/enums/permissions';

export interface IAuthService {
  setupUserInteractionListeners(): void;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService{

  private tokenRefreshTimer: any;
  private interactionTimer: any;
  private lastInteractionTime: number = Date.now(); // Guarda el tiempo de la última interacción

  boundHandleUserInteraction:any

  public user = new BehaviorSubject<DecryptedUser>(null as any);
  public user$ = this.user.asObservable().pipe(
    filter(user => user != null)
  );

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private _cognitoService:CognitoService,
    private rbacService:RbacService,
    private _deviceService:DeviceService,
    private usersService:UsersService,
    private dialogsService:DialogsService,
  ) {}

  setUser(user:DecryptedUser){
    this.user.next(user)
  }

  getCurrentUser(): DecryptedUser {
    return this.user.getValue();
  }

  /**
 * Configura listeners para registrar la interacción del usuario
 */
  public setupUserInteractionListeners(): void {
    this.removeUserInteractionListeners()
    // Captura clics y desplazamientos
    // Enlaza la función una vez para usarla en ambos lugares (add/remove)
    this.boundHandleUserInteraction = this.handleUserInteraction.bind(this);

    // Captura clics, movimientos del mouse y pulsaciones de teclado
    window.addEventListener('click', this.boundHandleUserInteraction);
    window.addEventListener('keyup', this.boundHandleUserInteraction);

    // Llama una vez para inicializar la detección de interacción
    this.handleUserInteraction();
  }

  public handleUserInteraction(): void {
    this.lastInteractionTime = Date.now();

    // Limpia el timeout anterior si existe
    if (this.interactionTimer) clearTimeout(this.interactionTimer);

    // Configura un nuevo timeout
    this.interactionTimer = setTimeout(() => {
      this.logout();
    }, 900000); // TO DO - Tiempo de inactividad
  }

  clearInteractionTimeout(){
    if (this.interactionTimer) clearTimeout(this.interactionTimer)
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer)
  }

  public removeUserInteractionListeners(): void {
    window.removeEventListener('click', this.boundHandleUserInteraction);
    window.removeEventListener('keyup', this.boundHandleUserInteraction);
  }

  async validateTerminalAccess(userId?: string): Promise<ResponseModel<UserTerminal> | null> {
    try {
      const serial = await this._deviceService.obtenerMacAddress();
      const id = userId ?? localStorage.getItem("UId")
      if (id && serial) {
        localStorage.setItem("DName",serial)
        const response: ResponseModel<UserTerminal> = await this.usersService.getTerminalUser(id, serial);

        // Formateador de fecha solo con año, mes y día (sin hora)
        const formatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Santo_Domingo',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });

        // Formatea la fecha actual del sistema
        const todayDate = formatter.format(new Date()); // Resultado: '2025-07-18'

        // Formatea la fecha esperada desde el backend también (por si viene con hora)
        const expectedDate = formatter.format(new Date(response.data.currentDate));

        // Compara solo las fechas (sin hora)
        if (todayDate !== expectedDate) {
          this.dialogsService.showError(
            'La fecha de la terminal es errónea',
            `Fecha de la Terminal: ${todayDate}<br>Fecha Del Sistema: ${expectedDate}`
          );
          this._cognitoService.signOut();
          return null;
        }

        this.user.next({...this.user.getValue(),UserName:response.data.userName})
        if (response.success) {
          localStorage.setItem("UTId",response.data.userTerminalId)
          localStorage.setItem("TId",response.data.terminalId)
          localStorage.setItem("BId",response.data.bankId)
          return response;
        } else {
          this.dialogsService.showError(response.errorMessage)
          this._cognitoService.signOut()
          return null;
        }
      } else {
        this.dialogsService.showError('User ID or Serial no esta definido.')
        this._cognitoService.signOut()
        return null;
      }
    } catch (error:any) {
      const errorMessage = error?.error?.errorMessage || 'An unexpected error occurred';
      this.dialogsService.showError(errorMessage);
      this._cognitoService.signOut()
      return null;
    }
  }

  public redirectIfAuthenticated(): void {
    this._cognitoService.isAuthenticated().then(
      (value) => {
        if (value) {
          this.router.navigate(['/Inicio']);
        }
      }
    );
  }

  public startTokenRefreshCheck(token?: string): void {
    // Obtiene el token de acceso desde los argumentos o del almacenamiento local.
    const accessToken = token ? token : localStorage.getItem('rbac-token');

    // Si no se encuentra el token, muestra una advertencia y cierra la sesión.
    if (!accessToken) {
        console.warn('Token de acceso no encontrado. Finalizando el proceso de renovación.');
        this._cognitoService.signOut(true);
        return;
    }

    let decodedToken: DecryptedUser;
    try {
        // Intenta decodificar el token de acceso para extraer la información del usuario.
        decodedToken = jwtDecode(accessToken);
    } catch (error) {
        // Si hay un error al decodificar, registra el error y cierra la sesión.
        console.error('Error al decodificar el token:', error);
        this._cognitoService.signOut(true);
        return;
    }

    // Verifica que el token decodificado sea válido y tenga un campo de expiración.
    if (!decodedToken || !decodedToken.exp) return;

    // Convierte el tiempo de expiración (en segundos) del token a un objeto de tipo Date.
    // console.log('expiresAt',this.convertMillisecondsToTimeFormat(decodedToken.exp * 1000))
    const expiresAt = new Date(decodedToken.exp * 1000);


    // Calcula el tiempo restante antes de la expiración del token,
    // descontando un margen de 1 minuto para renovar el token antes de que expire.
    const bufferTime = 1 * 60 * 1000; // 1 minuto en milisegundos
    const timeoutTime = 15 * 60 * 1000; // 1 minuto en milisegundos
    // console.log('bufferTime',this.convertMillisecondsToTimeFormat(bufferTime))
    const timeout = Math.max(expiresAt.getTime() - Date.now() - bufferTime, 0);
    // console.log('timeout',timeout,this.convertMillisecondsToTimeFormat(timeout))

    // Limpia cualquier temporizador previo antes de configurar uno nuevo para evitar duplicados.
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);

    // Si hay tiempo suficiente para renovar el token antes de que expire:
    if (timeout > 0) {
      // Configura un temporizador para manejar la interacción del usuario y verificar la validez del token.
      // console.log('crea timeout')
      this.tokenRefreshTimer = setTimeout(() => {
        // console.log('ejecuta timeout')
        // Verifica si el token ha expirado nuevamente antes de realizar cualquier acción.
        if (expiresAt.getTime() < Date.now()) {
            // console.warn('Token expirado inesperadamente.');
            this._cognitoService.signOut(true); // Cierra la sesión si el token ya expiró.
            return;
        }

        // Calcula el tiempo transcurrido desde la última interacción del usuario.
        const currentTime = Date.now();
        const timeSinceLastInteraction = currentTime - this.lastInteractionTime;

        // Si el usuario ha interactuado recientemente (dentro del margen de tiempo definido):
        if (timeSinceLastInteraction <= timeoutTime) {
            // Renueva el token porque el usuario está activo.
            this.refreshToken();
        } else {
            // Si el usuario no ha interactuado en el tiempo esperado, Mostrar Codigo para confirmar inactividad.
            // console.log('Si el usuario no ha interactuado en el tiempo esperado, Mostrar Codigo para confirmar inactividad')
            this.logout();
        }
      }, timeout);
    } else {
        // Si el token ya expiró, cierra la sesión inmediatamente.
        // console.log('Si el token ya expiró, cierra la sesión inmediatamente')
        this._cognitoService.signOut(true);
    }
  }

  async refreshUserData(token?: string) {
    try {
      // Determinamos el token de acceso
      const accessToken = token || localStorage.getItem("rbac-token");
      // Si no hay token disponible, cerrar la sesión
      if (!accessToken) {
        return null;
      }
      // Obtenemos los datos del token desde el servicio RBAC
      const user: DecryptedUser = jwtDecode(accessToken);
      if (user) {
        const data = await firstValueFrom(this.rbacService.getUserPermissions(user.UserApplicationId,true));
        // Actualizamos el usuario en la aplicación
        this.setUser({...user,permissions:data});
        return user; // Retornamos los datos del usuario
      } else {
        return null; // Si no hay datos, retornamos null
      }
    } catch (error) {
      // Manejo de errores
      console.error("Error al obtener los datos del token:", error);
      return null;
    }
  }


  /**
   * Refresca el token automáticamente
   */
  refreshToken(): void {
    // Aquí llamarías al backend para refrescar el token
    this.rbacService.refreshToken().pipe(
      retry(2)
    ).subscribe(
      {
        next:(value)=>{
          if(value.success && value.data){
            // console.log('refrescando token')
            localStorage.setItem('rbac-token', value.data.accessToken);
            localStorage.setItem('refresh-token', value.data.refreshToken);
            this.startTokenRefreshCheck(value.data.accessToken)
          }else{
            this._cognitoService.signOut(true)
          }
        },
        error:(error)=> {
          // if (error.status === 401) {
            this._cognitoService.signOut(true)
          // }
        },
      }
    )
  }

  /**
   * Cierra sesión por inactividad
   */
  private logout(): void {
    this.dialogsService.open(LogoutModalComponent,{
      data: {timeout:5},
      width: '35%',   // Ajusta el ancho
      maxWidth: '100vw',
      maxHeight: '100vh',
      disableClose:true
    });
  }

  convertMillisecondsToTimeFormat(milliseconds: number): string {
    if (isNaN(milliseconds) || milliseconds < 0) {
      throw new Error('El valor debe ser un número positivo.');
    }

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Aseguramos que los valores siempre tengan dos dígitos
    const pad = (num: number): string => num.toString().padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  hasPermission(requiredPermissions: string[], authMode: AuthMode = AuthMode.All): boolean {
    const user = this.getCurrentUser(); // Obtener usuario
    if (!user || !user.permissions) return false;

    return authMode === AuthMode.All
      ? requiredPermissions.every(permission => user.permissions.includes(permission)) // Debe tener TODOS los permisos
      : requiredPermissions.some(permission => user.permissions.includes(permission)); // Debe tener al menos UNO
  }
}
